import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { analyticsAPI } from '../services/api';
import { HiTrendingUp, HiCurrencyDollar, HiShoppingCart, HiClock, HiArrowUp, HiArrowDown, HiExternalLink } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useCarga } from '../hooks/useCarga';
import ErrorAlCargar from '../components/ErrorAlCargar';
import EstadoDeLaOperacion from '../components/EstadoDeLaOperacion';
import { dinero } from '../utils/economia';


export default function Dashboard() {
    const { user } = useAuthStore();
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const { cargando: loading, error, ejecutar, reintentar } = useCarga();
    const [period, setPeriod] = useState('30d');

    useEffect(() => {
        fetchDashboardData();
    }, [period]);

    const fetchDashboardData = () =>
        ejecutar(async () => {
            // El estado de la operación no depende del periodo elegido: «tienes
            // dos anuncios terminados» es cierto hoy, se miren 7 días o 90.
            const [panel, estado] = await Promise.all([
                analyticsAPI.getDashboard(period),
                analyticsAPI.getEstado()
            ]);
            setData({ ...panel.data.data, estado: estado.data.data });
        });

    // Calculate real percentage changes (show 0% or N/A when no data)
    const calculateChange = (current, previous) => {
        const curr = Number(current);
        const prev = Number(previous);

        // Check if values are 0, null, undefined, or NaN
        if (!curr || curr === 0 || isNaN(curr)) return null;
        if (!prev || prev === 0 || isNaN(prev)) return null;

        const changeVal = ((curr - prev) / prev) * 100;
        return {
            change: `${changeVal >= 0 ? '+' : ''}${changeVal.toFixed(1)}%`,
            type: changeVal >= 0 ? 'positive' : 'negative'
        };
    };

    const stats = [
        {
            name: t('dashboard.total_revenue'),
            value: data?.overview?.totalRevenue || 0,
            format: 'currency',
            icon: HiCurrencyDollar,
            color: 'from-green-500 to-emerald-500',
            ...(calculateChange(data?.overview?.totalRevenue, data?.overview?.previousRevenue) || {})
        },
        {
            name: t('dashboard.total_profit'),
            value: data?.overview?.totalProfit || 0,
            format: 'currency',
            icon: HiTrendingUp,
            color: 'from-blue-500 to-cyan-500',
            ...(calculateChange(data?.overview?.totalProfit, data?.overview?.previousProfit) || {})
        },
        {
            name: t('dashboard.total_orders'),
            value: data?.overview?.totalOrders || 0,
            format: 'number',
            icon: HiShoppingCart,
            color: 'from-purple-500 to-pink-500',
            ...(calculateChange(data?.overview?.totalOrders, data?.overview?.previousOrders) || {})
        },
        {
            name: t('dashboard.pending_orders'),
            value: data?.overview?.pendingOrders || 0,
            format: 'number',
            icon: HiClock,
            color: 'from-orange-500 to-amber-500',
            ...(calculateChange(data?.overview?.pendingOrders, data?.overview?.previousPending) || {})
        }
    ];

    const formatValue = (value, format) => {
        if (format === 'currency') {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
        }
        return new Intl.NumberFormat('en-US').format(value);
    };

    if (loading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-32 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
                    ))}
                </div>
            </div>
        );
    }

    // Un fallo de carga no puede verse como una cuenta a cero.
    if (error) {
        return (
            <div className="space-y-8">
                <h1 className="page-title">
                    {t('dashboard.welcome', { name: user?.full_name?.split(' ')[0] || 'User' })}
                </h1>
                <ErrorAlCargar error={error} onReintentar={reintentar} />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="page-title">{t('dashboard.welcome', { name: user?.full_name?.split(' ')[0] || 'User' })}</h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('dashboard.whats_happening')}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {['7d', '30d', '90d'].map((p) => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${period === p
                                ? 'bg-primary-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            {t(`analytics.period_${p}`)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Qué hay y qué lo frena. Va lo primero porque es lo único que
                dice algo mientras no haya ventas, que es ahora. */}
            <EstadoDeLaOperacion estado={data?.estado} />

            {/* El dinero, y solo cuando lo hay.
                Cuatro tarjetas a cero no informan de nada y encima se leen como
                una pérdida. Sin un solo pedido en el periodo, una frase honesta
                dice más y ocupa menos. */}
            {data?.overview?.totalOrders === 0 ? (
                <div className="card text-center py-8">
                    <p className="text-slate-600 dark:text-slate-300">{t('dashboard.no_sales_title')}</p>
                    <p className="text-sm text-slate-500 mt-1">{t('dashboard.no_sales_desc')}</p>
                </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, i) => (
                    <div key={i} className="card group hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-slate-500 dark:text-slate-400">{stat.name}</p>
                                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                                    {formatValue(stat.value, stat.format)}
                                </p>
                            </div>
                            <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                <stat.icon className="w-6 h-6 text-white" />
                            </div>
                        </div>
                        {/* Only show percentage change if there's actual data */}
                        {stat.change ? (
                            <div className="flex items-center mt-4">
                                {stat.type === 'positive' ? (
                                    <HiArrowUp className="w-4 h-4 text-green-500" />
                                ) : stat.type === 'negative' ? (
                                    <HiArrowDown className="w-4 h-4 text-red-500" />
                                ) : (
                                    <span className="w-4 h-4" />
                                )}
                                <span className={`text-sm font-medium ${stat.type === 'positive' ? 'text-green-500' :
                                    stat.type === 'negative' ? 'text-red-500' :
                                        'text-slate-400'
                                    }`}>
                                    {stat.change}
                                </span>
                                <span className="text-sm text-slate-500 dark:text-slate-400 ml-2">{t('dashboard.vs_last_period')}</span>
                            </div>
                        ) : (
                            <div className="flex items-center mt-4">
                                <span className="text-sm text-slate-400">{t('dashboard.no_data_yet')}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Link to="/products/search" className="card-hover flex items-center gap-4 group">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-primary-500">{t('dashboard.search_products')}</h3>
                        <p className="text-sm text-slate-500">{t('dashboard.search_products_desc')}</p>
                    </div>
                    <HiExternalLink className="w-5 h-5 text-slate-400 group-hover:text-primary-500" />
                </Link>

                <Link to="/products/winning" className="card-hover flex items-center gap-4 group">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-amber-500">{t('dashboard.ai_winning_products')}</h3>
                        <p className="text-sm text-slate-500">{t('dashboard.ai_winning_products_desc')}</p>
                    </div>
                    <HiExternalLink className="w-5 h-5 text-slate-400 group-hover:text-amber-500" />
                </Link>

                <Link to="/stores" className="card-hover flex items-center gap-4 group">
                    <div className="w-14 h-14 bg-gradient-to-br from-accent-500 to-pink-500 rounded-xl flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-accent-500">{t('dashboard.connect_store')}</h3>
                        <p className="text-sm text-slate-500">{t('dashboard.connect_store_desc')}</p>
                    </div>
                    <HiExternalLink className="w-5 h-5 text-slate-400 group-hover:text-accent-500" />
                </Link>
            </div>

            {/* Recent Orders */}
            <div className="card">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="section-title">{t('dashboard.recent_orders')}</h2>
                    <Link to="/orders" className="text-sm text-primary-500 hover:text-primary-600">
                        {t('dashboard.view_all')}
                    </Link>
                </div>

                {data?.recentOrders?.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-sm text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                                    <th className="pb-3 font-medium">{t('dashboard.col_order')}</th>
                                    <th className="pb-3 font-medium">{t('dashboard.col_store')}</th>
                                    <th className="pb-3 font-medium">{t('dashboard.col_amount')}</th>
                                    <th className="pb-3 font-medium">{t('dashboard.col_profit')}</th>
                                    <th className="pb-3 font-medium">{t('dashboard.col_status')}</th>
                                    <th className="pb-3 font-medium">{t('dashboard.col_date')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {data.recentOrders.map((order) => (
                                    <tr key={order.id} className="text-sm">
                                        <td className="py-3 font-medium text-slate-900 dark:text-white">
                                            #{order.order_number || order.id.slice(0, 8)}
                                        </td>
                                        <td className="py-3 text-slate-600 dark:text-slate-400">
                                            {order.store?.store_name || 'N/A'}
                                        </td>
                                        {/* Esta tabla y la de Pedidos enseñaban
                                            el mismo pedido de dos maneras: allí
                                            «sin calcular», aquí «+$0.00» en
                                            verde. Tres mentiras en dos celdas:
                                            un total desconocido como cero, una
                                            ganancia desconocida como cero, y una
                                            pérdida escrita «+$-5.43», en verde y
                                            con el signo delante del símbolo.
                                            `dinero()` existe justo para lo
                                            último y no se usaba. */}
                                        <td className="py-3 text-slate-900 dark:text-white">
                                            {order.total_amount === null || order.total_amount === undefined
                                                ? <span className="text-slate-400">{t('orders.total_unknown')}</span>
                                                : dinero(Number(order.total_amount))}
                                        </td>
                                        <td className="py-3 font-medium">
                                            {order.profit === null || order.profit === undefined ? (
                                                <span className="text-slate-400">{t('orders.profit_unknown')}</span>
                                            ) : (
                                                <span className={Number(order.profit) >= 0 ? 'text-green-500' : 'text-red-500'}>
                                                    {dinero(Number(order.profit))}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <span className={`badge ${order.status === 'delivered' ? 'badge-success' :
                                                order.status === 'shipped' ? 'badge-info' :
                                                    order.status === 'pending' ? 'badge-warning' :
                                                        'badge-danger'
                                                }`}>
                                                {/* Estaba crudo: «awaiting_shipment».
                                                    La tabla de Pedidos sí lo traduce. */}
                                                {t(`orders.status_${order.status}`)}
                                            </span>
                                        </td>
                                        <td className="py-3 text-slate-500">
                                            {new Date(order.created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12 text-slate-500">
                        <svg className="w-12 h-12 mx-auto mb-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <p>{t('dashboard.no_orders_yet')}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
